import React, { useState, useMemo } from 'react';
import { X, Mail, Search, Building2, Briefcase, MessageSquare, AlertCircle } from 'lucide-react';
import { useEmailsHub } from '../hooks/useEmailsHub';
import { formatDate } from '../utils/dateHelpers';

const PAPEL_COLORS = {
  compradora: 'bg-blue-100 text-blue-800',
  proprietaria: 'bg-amber-100 text-amber-800',
  remetente: 'bg-emerald-100 text-emerald-800',
  destinatario: 'bg-sky-100 text-sky-800',
  from: 'bg-purple-100 text-purple-800',
  para: 'bg-teal-100 text-teal-800',
  cc: 'bg-cyan-100 text-cyan-800',
  assunto: 'bg-indigo-100 text-indigo-800',
  corpo: 'bg-slate-100 text-slate-800',
  oferta: 'bg-green-100 text-green-800',
};

const PAPEL_LABEL = {
  compradora: '🛒 Compradora',
  proprietaria: '🏠 Proprietária',
  remetente: '✉️ Enviou',
  destinatario: '📨 Recebeu',
  from: 'Remetente',
  para: 'Destinatário',
  cc: 'Cópia',
  assunto: 'No assunto',
  corpo: 'No texto',
  oferta: 'Oferta',
};

/**
 * EmailsHubModal — modal unificado para acesso a emails em 3 contextos.
 *
 * Props (exatamente um modo):
 *   - { modo: 'empresa', empresaId, empresaNome, onClose, onOpenEmail, onOpenOferta }
 *   - { modo: 'oferta', ofertaId, ofertaNome, onClose, onOpenEmail }
 *   - { modo: 'busca', onClose, onOpenEmail }
 *
 * `onOpenEmail(email)` — callback para abrir EmailDetailDrawer do email clicado
 * `onOpenOferta(oferta)` — (opcional) callback para abrir OfertaDetailModal
 */
const EmailsHubModal = ({ 
  modo, 
  empresaId, empresaNome, 
  ofertaId, ofertaNome, 
  onClose, 
  onOpenEmail,
  onOpenOferta,
}) => {
  const [termoBusca, setTermoBusca] = useState('');
  const [visao, setVisao] = useState('lista'); // lista | thread | oferta
  const [filtroTipoConteudo, setFiltroTipoConteudo] = useState(null);

  const { emails, emailsPorOferta, emailsPorThread, totais, loading, error } = useEmailsHub(
    modo === 'busca' 
      ? { modo: 'busca', termo: termoBusca }
      : modo === 'empresa' 
      ? { modo: 'empresa', empresaId }
      : { modo: 'oferta', ofertaId }
  );

  const emailsFiltrados = useMemo(() => {
    let lista = emails;
    if (filtroTipoConteudo) lista = lista.filter(e => e.tipo_conteudo === filtroTipoConteudo);
    return lista;
  }, [emails, filtroTipoConteudo]);

  // Facetas para filtros dinâmicos
  const facetasTipoConteudo = useMemo(() => {
    const counts = {};
    emails.forEach(e => {
      if (e.tipo_conteudo) counts[e.tipo_conteudo] = (counts[e.tipo_conteudo] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [emails]);

  const tituloModal = {
    empresa: `Emails — ${empresaNome || 'Empresa'}`,
    oferta: `Emails — ${ofertaNome || 'Oferta'}`,
    busca: 'Busca global de emails',
  }[modo];

  const iconeModal = {
    empresa: Building2,
    oferta: Briefcase,
    busca: Search,
  }[modo];
  const IconeModal = iconeModal;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[10001] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-[95vw] 2xl:max-w-7xl max-h-[95vh] flex flex-col">
        
        {/* Header */}
        <div className="p-4 border-b border-slate-200 flex-shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-2 flex-grow min-w-0">
              <div className="bg-indigo-100 p-1.5 rounded-lg flex-shrink-0">
                <IconeModal className="text-indigo-600" size={18} />
              </div>
              <div className="min-w-0">
                <div className="text-[11px] text-slate-500 uppercase tracking-wider">Hub de emails</div>
                <div className="text-sm font-bold text-slate-800 truncate">{tituloModal}</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {loading ? 'Carregando...' : `${totais.total} email${totais.total !== 1 ? 's' : ''}`}
                  {emails.length > 0 && ` · ${emailsPorThread.length} thread${emailsPorThread.length !== 1 ? 's' : ''}`}
                </div>
              </div>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600 flex-shrink-0">
              <X size={20} />
            </button>
          </div>

          {/* Busca — só no modo 'busca' */}
          {modo === 'busca' && (
            <div className="mt-3 relative">
              <Search size={14} className="absolute left-3 top-2.5 text-slate-400" />
              <input
                type="text"
                value={termoBusca}
                onChange={(e) => setTermoBusca(e.target.value)}
                placeholder="Buscar em remetente, assunto, corpo, destinatários..."
                className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                autoFocus
              />
              {termoBusca.length > 0 && termoBusca.length < 2 && (
                <div className="text-[10px] text-slate-500 mt-1">Digite pelo menos 2 caracteres...</div>
              )}
            </div>
          )}

          {/* Controles de visão (só quando há emails) */}
          {emails.length > 0 && (
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <span className="text-[10px] text-slate-500 uppercase tracking-wider">Visão:</span>
              {[
                { key: 'lista', label: 'Lista', icon: Mail },
                { key: 'thread', label: 'Por thread', icon: MessageSquare },
                ...(modo === 'empresa' ? [{ key: 'oferta', label: 'Por oferta', icon: Briefcase }] : []),
              ].map(v => {
                const Icon = v.icon;
                return (
                  <button
                    key={v.key}
                    onClick={() => setVisao(v.key)}
                    className={`text-[11px] px-2 py-1 rounded flex items-center gap-1 transition-colors ${
                      visao === v.key 
                        ? 'bg-indigo-600 text-white' 
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    <Icon size={10} /> {v.label}
                  </button>
                );
              })}

              {/* Filtros rápidos tipo_conteudo */}
              {facetasTipoConteudo.length > 0 && (
                <div className="ml-auto flex items-center gap-1 flex-wrap">
                  <span className="text-[10px] text-slate-500">Tipo:</span>
                  <button
                    onClick={() => setFiltroTipoConteudo(null)}
                    className={`text-[10px] px-1.5 py-0.5 rounded ${filtroTipoConteudo === null ? 'bg-slate-700 text-white' : 'bg-slate-100 text-slate-600'}`}
                  >
                    todos
                  </button>
                  {facetasTipoConteudo.slice(0, 5).map(([t, n]) => (
                    <button
                      key={t}
                      onClick={() => setFiltroTipoConteudo(filtroTipoConteudo === t ? null : t)}
                      className={`text-[10px] px-1.5 py-0.5 rounded ${
                        filtroTipoConteudo === t ? 'bg-slate-700 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      {t} ({n})
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Corpo scrollable */}
        <div className="flex-grow overflow-y-auto p-4">
          {loading && <LoadingState />}
          {error && <ErrorState msg={error} />}
          {!loading && !error && emails.length === 0 && modo !== 'busca' && (
            <EmptyState modo={modo} />
          )}
          {!loading && !error && modo === 'busca' && termoBusca.length < 2 && (
            <div className="text-center py-16 text-sm text-slate-500">
              <Search size={32} className="mx-auto mb-2 text-slate-300" />
              Digite no campo acima para buscar.
            </div>
          )}
          {!loading && !error && modo === 'busca' && termoBusca.length >= 2 && emails.length === 0 && (
            <div className="text-center py-16 text-sm text-slate-500">
              Nenhum email encontrado com "{termoBusca}".
            </div>
          )}

          {!loading && !error && emails.length > 0 && (
            <>
              {visao === 'lista' && (
                <ListaEmails emails={emailsFiltrados} onOpenEmail={onOpenEmail} />
              )}
              {visao === 'thread' && (
                <VisaoThread threads={emailsPorThread} onOpenEmail={onOpenEmail} />
              )}
              {visao === 'oferta' && (
                <VisaoOferta grupos={emailsPorOferta} onOpenEmail={onOpenEmail} onOpenOferta={onOpenOferta} />
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-slate-200 bg-slate-50 flex-shrink-0 flex items-center justify-between text-[10px] text-slate-500">
          <span>
            {modo === 'empresa' && 'Mostra emails onde a empresa aparece como compradora, proprietária, ou pelo domínio dos contatos.'}
            {modo === 'oferta' && 'Todos os emails vinculados a esta oferta.'}
            {modo === 'busca' && 'Busca em remetente, assunto e corpo do email. Limitado aos 100 mais recentes.'}
          </span>
          <button onClick={onClose} className="px-3 py-1 text-xs text-slate-600 hover:bg-slate-200 rounded">
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Sub-componentes
// ============================================================================

const LoadingState = () => (
  <div className="text-center py-16 text-sm text-slate-500">Carregando emails...</div>
);

const ErrorState = ({ msg }) => (
  <div className="p-3 bg-red-50 border border-red-200 rounded flex items-start gap-2">
    <AlertCircle size={14} className="text-red-600 flex-shrink-0 mt-0.5" />
    <span className="text-xs text-red-800">{msg}</span>
  </div>
);

const EmptyState = ({ modo }) => (
  <div className="text-center py-16">
    <Mail size={32} className="mx-auto mb-2 text-slate-300" />
    <div className="text-sm text-slate-600">
      {modo === 'empresa' && 'Nenhum email encontrado para esta empresa.'}
      {modo === 'oferta' && 'Nenhum email vinculado a esta oferta.'}
    </div>
  </div>
);

const ListaEmails = ({ emails, onOpenEmail }) => (
  <div className="space-y-1.5">
    {emails.map(em => <EmailCard key={em.id} email={em} onClick={() => onOpenEmail(em)} />)}
  </div>
);

const VisaoThread = ({ threads, onOpenEmail }) => (
  <div className="space-y-3">
    {threads.map(t => (
      <div key={t.threadId} className="border border-slate-200 rounded overflow-hidden">
        <div className="bg-slate-50 px-3 py-1.5 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <MessageSquare size={12} className="text-slate-500 flex-shrink-0" />
            <span className="text-xs font-semibold text-slate-800 truncate">
              {t.subjectBase || '(sem assunto)'}
            </span>
          </div>
          <span className="text-[10px] text-slate-500 flex-shrink-0">
            {t.emails.length} email{t.emails.length !== 1 ? 's' : ''} · 
            {' '}{formatDate(t.primeiroEmail.data_envio)} → {formatDate(t.ultimoEmail.data_envio)}
          </span>
        </div>
        <div className="divide-y divide-slate-100">
          {t.emails.map(em => (
            <EmailCard key={em.id} email={em} onClick={() => onOpenEmail(em)} compact />
          ))}
        </div>
      </div>
    ))}
  </div>
);

const VisaoOferta = ({ grupos, onOpenEmail, onOpenOferta }) => (
  <div className="space-y-3">
    {grupos.map(g => (
      <div key={g.oferta_id || 'sem_oferta'} className="border border-slate-200 rounded overflow-hidden">
        <div className="bg-indigo-50 px-3 py-1.5 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <Briefcase size={12} className="text-indigo-600 flex-shrink-0" />
            <span className="text-xs font-semibold text-indigo-900 truncate">{g.oferta_nome}</span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-[10px] text-indigo-700">{g.emails.length} email{g.emails.length !== 1 ? 's' : ''}</span>
            {g.oferta_id && onOpenOferta && (
              <button
                onClick={() => onOpenOferta({ id: g.oferta_id, nome: g.oferta_nome })}
                className="text-[10px] px-1.5 py-0.5 bg-indigo-600 text-white rounded hover:bg-indigo-700"
              >
                Abrir oferta →
              </button>
            )}
          </div>
        </div>
        <div className="divide-y divide-slate-100">
          {g.emails.slice(0, 10).map(em => (
            <EmailCard key={em.id} email={em} onClick={() => onOpenEmail(em)} compact />
          ))}
          {g.emails.length > 10 && (
            <div className="px-3 py-1.5 text-[11px] text-slate-500 bg-slate-50">
              + {g.emails.length - 10} email{g.emails.length - 10 !== 1 ? 's' : ''} mais nesta oferta (use visão "Lista" para ver todos)
            </div>
          )}
        </div>
      </div>
    ))}
  </div>
);

const EmailCard = ({ email, onClick, compact }) => {
  const empresaNome = email.empresas?.empresa || email.empresa_proprietaria?.empresa;
  const areaNome = email.areas?.nome;
  const papeis = email._papeis || [];

  return (
    <div 
      onClick={onClick}
      className={`px-3 py-2 hover:bg-indigo-50/50 cursor-pointer transition-colors ${compact ? '' : 'border border-slate-200 rounded mb-0'}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-grow min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[10px] text-slate-400">
              {email.direcao === 'entrada' ? '📥' : '📤'}
            </span>
            {empresaNome && (
              <span className="text-[11px] font-semibold text-slate-700">{empresaNome}</span>
            )}
            {papeis.map(p => (
              <span key={p} className={`text-[9px] px-1 py-0.5 rounded ${PAPEL_COLORS[p] || 'bg-slate-100 text-slate-600'}`}>
                {PAPEL_LABEL[p] || p}
              </span>
            ))}
            {email.tipo_conteudo && (
              <span className="text-[9px] px-1 py-0.5 rounded bg-violet-100 text-violet-800">
                {email.tipo_conteudo}
              </span>
            )}
            <span className="text-[10px] text-slate-400 ml-auto flex-shrink-0">
              {formatDate(email.data_envio)}
            </span>
          </div>
          <div className="text-xs text-slate-800 truncate mt-0.5 font-medium">
            {email.subject || '(sem assunto)'}
          </div>
          <div className="text-[10px] text-slate-500 truncate mt-0.5">
            <span className="text-slate-400">De:</span> {email.from_email}
            {email.to_emails && email.to_emails.length > 0 && (
              <> · <span className="text-slate-400">Para:</span> {
                email.to_emails.length > 2 
                  ? `${email.to_emails.slice(0, 2).join(', ')} +${email.to_emails.length - 2}`
                  : email.to_emails.join(', ')
              }</>
            )}
          </div>
          {areaNome && (
            <div className="text-[10px] text-indigo-600 truncate mt-0.5">
              📍 {areaNome}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailsHubModal;
